import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Product } from '../../../types/product';
import WishlistButton from '../../../components/base/WishlistButton';
import ShippingCalculator from './ShippingCalculator';

function isSameDayEligible(): boolean {
  const now = new Date();
  const estOffset = -5;
  const utcHours = now.getUTCHours();
  const estHours = (utcHours + estOffset + 24) % 24;
  const estDay = new Date(now.getTime() + estOffset * 60 * 60 * 1000).getUTCDay();
  return estDay >= 1 && estDay <= 5 && estHours < 14;
}

interface ProductInfoProps {
  product: Product;
  onAddToCart: (qty: number) => void;
}

export default function ProductInfo({ product, onAddToCart }: ProductInfoProps) {
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  const isPerfume = product.category === 'PERFUME';
  
  const isSoldOut = product.stock_quantity === 0;
  const isLowStock = product.stock_quantity > 0 && product.stock_quantity <= 5;

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={`full-${i}`} className="ri-star-fill text-[#2E7D32]"></i>);
    }
    if (hasHalf) {
      stars.push(<i key="half" className="ri-star-half-fill text-[#2E7D32]"></i>);
    }
    const remaining = 5 - fullStars - (hasHalf ? 1 : 0);
    for (let i = 0; i < remaining; i++) {
      stars.push(<i key={`empty-${i}`} className="ri-star-line text-gray-300"></i>);
    }
    return stars;
  };

  const handleIncrement = () => {
    if (quantity < product.stock_quantity) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  return (
    <div className="flex flex-col justify-center">
      <p className="text-xs text-gray-500 tracking-[0.25em] mb-3 uppercase">{product.category}</p>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 mb-4 leading-tight">{product.name}</h1>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-1 text-sm">{renderStars(product.rating)}</div>
        <span className="text-sm font-medium text-gray-900">{product.rating}</span>
        <span className="text-sm text-gray-400">|</span>
        <span className="text-sm text-gray-500">{product.review_count} reviews</span>
      </div>

      <p className="text-2xl sm:text-3xl font-serif text-gray-900 mb-4">${product.price.toFixed(2)}</p>

      {/* Stock Status */}
      <div className="mb-6">
        {product.stock_quantity === 0 ? (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
            <i className="ri-close-circle-line text-red-500"></i>
            <span className="text-sm font-semibold text-red-700">OUT OF STOCK</span>
          </div>
        ) : product.stock_quantity <= 5 ? (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-lg">
            <i className="ri-error-warning-line text-orange-500"></i>
            <span className="text-sm font-semibold text-orange-700">
              Only {product.stock_quantity} in stock
            </span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            <i className="ri-checkbox-circle-line text-emerald-500"></i>
            <span className="text-sm font-semibold text-emerald-700">
              {product.stock_quantity} units available
            </span>
          </div>
        )}
      </div>

      <p className="text-sm text-gray-600 leading-relaxed mb-8">{product.long_description}</p>

      {/* Scent Notes for Perfumes */}
      {isPerfume && product.top_notes && product.top_notes.length > 0 && (
        <div className="mb-8 p-4 sm:p-5 bg-white rounded-xl border border-gray-100">
          <h3 className="text-xs font-semibold tracking-[0.2em] text-gray-400 mb-4 uppercase">Fragrance Profile</h3>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <span className="text-xs font-semibold text-gray-900 sm:w-16 pt-0.5 shrink-0">TOP</span>
              <div className="flex flex-wrap gap-2">
                {product.top_notes.map((note) => (
                  <span key={note} className="px-3 py-1 bg-[#E8F5E9] text-gray-700 text-xs rounded-full">{note}</span>
                ))}
              </div>
            </div>
            {product.heart_notes && product.heart_notes.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                <span className="text-xs font-semibold text-gray-900 sm:w-16 pt-0.5 shrink-0">HEART</span>
                <div className="flex flex-wrap gap-2">
                  {product.heart_notes.map((note) => (
                    <span key={note} className="px-3 py-1 bg-[#E8F5E9] text-gray-700 text-xs rounded-full">{note}</span>
                  ))}
                </div>
              </div>
            )}
            {product.base_notes && product.base_notes.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                <span className="text-xs font-semibold text-gray-900 sm:w-16 pt-0.5 shrink-0">BASE</span>
                <div className="flex flex-wrap gap-2">
                  {product.base_notes.map((note) => (
                    <span key={note} className="px-3 py-1 bg-[#E8F5E9] text-gray-700 text-xs rounded-full">{note}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Size */}
      <div className="flex items-center gap-3 mb-8">
        <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase">Size:</span>
        <span className="px-4 py-2 border-2 border-[#2E7D32] rounded-lg text-sm font-medium text-gray-900">{product.size}</span>
      </div>

      {/* Quantity & Add to Cart */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-8">
        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden w-full sm:w-auto">
          <button
            onClick={handleDecrement}
            disabled={isSoldOut || quantity <= 1}
            className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-subtract-line"></i>
          </button>
          <span className="flex-1 sm:w-12 h-11 flex items-center justify-center text-sm font-semibold border-x border-gray-300">{quantity}</span>
          <button
            onClick={handleIncrement}
            disabled={isSoldOut || quantity >= product.stock_quantity}
            className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>

        <button
          onClick={() => !isSoldOut && onAddToCart(quantity)}
          disabled={isSoldOut}
          className="flex-1 py-3 bg-[#2E7D32] text-white text-sm font-semibold rounded-lg hover:bg-[#1B5E20] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-shopping-cart-line"></i>
          {isSoldOut ? 'OUT OF STOCK' : 'ADD TO CART'}
        </button>
      </div>

      {!isSoldOut && (
        <button
          onClick={() => {
            onAddToCart(quantity);
            navigate('/payment');
          }}
          className="w-full py-3 bg-[#1B5E20] text-white text-sm font-semibold rounded-lg hover:bg-[#2E7D32] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mb-8"
        >
          BUY NOW
        </button>
      )}

      {/* Shipping Rates */}
      <div className="mb-8 rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-truck-line text-[#2E7D32]"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Shipping Options</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {/* Standard */}
          <div className="flex items-center gap-4 px-5 py-3.5">
            <div className="w-8 h-8 rounded-lg bg-[#F1F8E9] flex items-center justify-center flex-shrink-0">
              <i className="ri-truck-line text-[#2E7D32] text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Standard Shipping</p>
              <p className="text-xs text-gray-500 mt-0.5">5–8 business days</p>
            </div>
            <div className="text-right flex-shrink-0">
              {product.price >= 200 ? (
                <span className="text-sm font-semibold text-[#2E7D32]">FREE</span>
              ) : (
                <span className="text-sm font-semibold text-gray-900">$4.99 – $9.99</span>
              )}
            </div>
          </div>
          {/* Express */}
          <div className="flex items-center gap-4 px-5 py-3.5">
            <div className="w-8 h-8 rounded-lg bg-[#F1F8E9] flex items-center justify-center flex-shrink-0">
              <i className="ri-rocket-line text-[#2E7D32] text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-gray-900">Express Shipping</p>
                <span className="text-[9px] font-bold tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">POPULAR</span>
                {isSameDayEligible() && (
                  <span className="text-[9px] font-bold tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span></span>
                    SAME-DAY
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">2–4 business days</p>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="text-sm font-semibold text-gray-900">$7.99 – $17.99</span>
            </div>
          </div>
          {/* Overnight */}
          <div className="flex items-center gap-4 px-5 py-3.5">
            <div className="w-8 h-8 rounded-lg bg-[#F1F8E9] flex items-center justify-center flex-shrink-0">
              <i className="ri-flashlight-line text-[#2E7D32] text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-gray-900">Overnight Shipping</p>
                {isSameDayEligible() && (
                  <span className="text-[9px] font-bold tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span></span>
                    SAME-DAY
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Next business day</p>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="text-sm font-semibold text-gray-900">$14.99 – $29.99</span>
            </div>
          </div>
        </div>
        <div className="px-5 py-2.5 bg-[#F1F8E9] border-t border-[#C8E6C9]">
          <p className="text-xs text-[#2E7D32] flex items-center gap-1.5">
            <i className="ri-gift-line text-sm"></i>
            <span><strong>Free standard shipping</strong> on orders over $200 · Exact rate calculated at checkout</span>
          </p>
        </div>
      </div>

      {/* Shipping Calculator by State */}
      <ShippingCalculator productPrice={product.price} productId={product.id} />

      {/* Trust Badges */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 lg:gap-6 pt-6 border-t border-gray-200">
        <WishlistButton
          product={{
            id: product.id,
            name: product.name,
            price: product.price,
            image: product.image,
            category: product.category,
          }}
          size="md"
          showLabel
          className="!bg-transparent !text-gray-500 hover:!text-red-500 !w-auto !h-auto gap-1"
        />
        <div className="hidden sm:block w-px h-5 bg-gray-200"></div>
        <div className="flex items-center gap-2 text-gray-500">
          <i className="ri-leaf-line text-lg"></i>
          <span className="text-xs whitespace-nowrap">100% Natural</span>
        </div>
        <div className="hidden sm:block w-px h-5 bg-gray-200"></div>
        <div className="flex items-center gap-2 text-gray-500">
          <i className="ri-refresh-line text-lg"></i>
          <span className="text-xs whitespace-nowrap">30-Day Returns</span>
        </div>
        <div className="hidden sm:block w-px h-5 bg-gray-200"></div>
        <div className="w-full sm:w-auto flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#E8F5E9] rounded-lg border border-[#2E7D32]/20">
          <i className="ri-truck-line text-base sm:text-lg text-[#2E7D32] shrink-0"></i>
          <span className="text-xs sm:text-sm text-gray-700">
            <strong className="text-[#2E7D32]">Free Shipping $200+</strong> · Arrives in 5–7 days
          </span>
        </div>
      </div>
    </div>
  );
}
